export const GENDERS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "NONBINARY", label: "Non-binary" },
  { value: "PREFER_NOT", label: "Prefer not to say" },
] as const;

export const OCCUPATIONS = [
  { value: "STUDENT", label: "Student" },
  { value: "WORKING", label: "Working professional" },
  { value: "OTHER", label: "Other" },
] as const;

export const PROPERTY_TYPES = [
  { value: "APARTMENT", label: "Apartment" },
  { value: "HOUSE", label: "House" },
  { value: "CONDO", label: "Condo" },
  { value: "BASEMENT", label: "Basement unit" },
  { value: "TOWNHOUSE", label: "Townhouse" },
] as const;

export const ROOM_TYPES = [
  { value: "PRIVATE", label: "Private room" },
  { value: "SHARED", label: "Shared room" },
] as const;

/**
 * Room/space a listing photo shows. Drives the landlord's per-photo picker and
 * the grouped "photo tour" in the gallery. Mirrors the PhotoCategory enum in
 * prisma/schema.prisma - change both together.
 *
 * Ordered the way a renter walks a place, not alphabetically, so the tour reads
 * as a tour. OTHER is last and is the default for every existing photo.
 */
export const PHOTO_CATEGORIES = [
  { value: "BEDROOM", label: "Bedroom" },
  { value: "BATHROOM", label: "Bathroom" },
  { value: "KITCHEN", label: "Kitchen" },
  { value: "LIVING_AREA", label: "Living area" },
  { value: "DINING", label: "Dining" },
  { value: "LAUNDRY", label: "Laundry" },
  { value: "PARKING", label: "Parking" },
  { value: "EXTERIOR", label: "Exterior" },
  { value: "BUILDING_AMENITY", label: "Building amenity" },
  { value: "OTHER", label: "Other" },
] as const;

export type PhotoCategoryValue = (typeof PHOTO_CATEGORIES)[number]["value"];

/** Lookup used by the gallery to turn a stored enum value into a heading. */
export const PHOTO_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  PHOTO_CATEGORIES.map((c) => [c.value, c.label]),
);

/** Photos per property. Enforced in the server action; the uploader mirrors it. */
export const IMAGE_MAX_PER_PROPERTY = 20;

/** Matches the property-images bucket file_size_limit in prisma/sql/0004_storage_limits.sql. */
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** Matches the bucket's allowed_mime_types in the same file. */
export const IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

export const IMAGE_ACCEPT = IMAGE_MIME_TYPES.join(",");

export const GENDER_RULES = [
  { value: "ANY", label: "Any gender" },
  { value: "MALE_ONLY", label: "Male only" },
  { value: "FEMALE_ONLY", label: "Female only" },
  { value: "SAME_AS_CURRENT", label: "Same as current occupant" },
] as const;

export const AMENITIES = [
  "Wi-Fi",
  "Laundry",
  "Parking",
  "Furnished",
  "Air conditioning",
  "Heating",
  "Utilities included",
  "Kitchen",
  "Private bathroom",
  "Gym",
  "Dishwasher",
  "Pet friendly",
] as const;

// ─── Property video limits ───────────────────────────────────────────────────
// Single source of truth for the uploader (UX), the server action (security), and
// prisma/sql/0012_storage_videos.sql (the only limit an attacker can't bypass,
// since the browser uploads straight to Supabase Storage). Change all three together.

export const VIDEO_MAX_BYTES = 100 * 1024 * 1024; // 100 MB - matches the bucket's file_size_limit
export const VIDEO_MAX_PER_PROPERTY = 5;
export const VIDEO_MAX_DURATION_SEC = 180; // 3 minutes

/** Accepted upload types. `video/quicktime` is what browsers report for .mov. */
export const VIDEO_MIME_TYPES = ["video/mp4", "video/quicktime", "video/webm"] as const;

export type VideoMimeType = (typeof VIDEO_MIME_TYPES)[number];

/** `accept` attribute for the file input. Extensions included because some
 *  browsers report an empty/odd MIME for .mov and would otherwise hide the file. */
export const VIDEO_ACCEPT = [...VIDEO_MIME_TYPES, ".mp4", ".mov", ".webm"].join(",");

/** True if a browser-reported MIME type is an accepted video type.
 *  A type predicate, so callers that guard on it can pass the value straight to
 *  the server action without re-asserting the narrowed type. */
export function isAllowedVideoMime(mime: string): mime is VideoMimeType {
  return (VIDEO_MIME_TYPES as readonly string[]).includes(mime);
}

/** Human-readable size, e.g. 104857600 -> "100 MB". Used in validation messages. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb % 1 === 0 ? mb : mb.toFixed(1)} MB`;
}

export const HOUSE_RULES = [
  "No smoking",
  "No pets",
  "No parties",
  "Quiet hours",
  "Guests allowed",
  "Students only",
] as const;

export const ONTARIO_CITIES = [
  "Brampton",
  "Toronto",
  "Mississauga",
  "Hamilton",
  "Waterloo",
  "Kitchener",
  "London",
  "Ottawa",
  "Guelph",
  "Oshawa",
] as const;
